/**
 * Sound manifest — maps game event IDs to one or more audio file variants.
 *
 * Authoring workflow (for JB):
 *   1. Generate sound in Suno using a prompt from `docs/SOUND_GENERATION_TASKS.md`.
 *   2. Drop the resulting .wav/.mp3 into the project root with Suno's
 *      auto-generated long filename (which starts with the prompt's first
 *      few words).
 *   3. Run `scripts/process_sounds.sh` — moves the file into the right
 *      subdirectory, applies big fade-in/out, normalizes to -14 LUFS,
 *      converts to .mp3 192k.
 *   4. If a new event is added, register it here.
 *
 * Each event has 1..N variants. The player picks one at random per play
 * with anti-repeat. See `player.ts` for the variant + polyphony logic.
 *
 * Volume is event-specific. UI sounds default louder; ambient drones
 * default much quieter so they sit under everything else.
 *
 * Tier (doc 22):
 *   0 = bed (ambient loops — always playing, never ducked, never duck others)
 *   1 = breath (atmospheric layer — random, fades, ducks self when busier)
 *   2 = feedback (tap, cooldown — every reactive sound)
 *   3 = event (lesson, guide appears, small upgrade — narrative moments)
 *   4 = peak (crystallize, epoch, big upgrade — священные моменты)
 *   5 = voice (Sai activation — высший приоритет, заглушает всё)
 */

export type SoundEventId =
  // UI — short reactive sounds
  | 'tap_click' // мини-click на КАЖДЫЙ тап (accepted + rejected) — даёт haptic-like feedback
  | 'tap_ding'
  | 'tap_ding_streak'
  | 'cooldown_ready'
  | 'cooldown_charging'
  | 'crystal_chord'
  | 'epoch_transition'
  | 'lesson_unlocked'
  // Events — narrative/world moments
  | 'golden_spark_appears'
  | 'golden_spark_caught'
  | 'guide_appears'
  // TODO: 'eclipse_swell' — re-generate in Suno (Эпоха 3+ Затмение mechanic)
  | 'sunrise_warm'
  | 'season_arrives'
  // Voice — Sai activation accompaniment
  | 'sai_activation_start'
  | 'activation_text_typing'
  // Ambient drones — looping per-epoch backgrounds
  | 'ambient_epoch1'
  | 'ambient_epoch2'
  | 'ambient_epoch3'
  // Upgrades — Visual Sprint 2 + doc 14
  | 'small_upgrade_purchased'
  | 'big_upgrade_purchased'
  | 'visible_change_reveal'
  // Atmospheric layer (doc 20 §4, doc 22 §1.2) — random "breath" between
  // ambient and foreground; scheduler triggers every 8-20 seconds.
  | 'atmospheric_breath';

export interface SoundDefinition {
  /** Paths relative to `apps/web/public`, e.g. `/sounds/ui/tap_ding/v01.mp3`. */
  readonly variants: readonly string[];
  /** Default volume 0..1. Tuned per category — see `docs/11` §Технические требования. */
  readonly volume: number;
  /** Whether this sound loops (only true for ambient drones). */
  readonly loop?: boolean;
  /** Sub-category for grouping in settings UI. */
  readonly category: 'ui' | 'events' | 'voice' | 'ambient' | 'upgrades';

  /**
   * Mixing priority tier (doc 22 §2). Drives ducking, gating, and the
   * global polyphony cap. The full table:
   *   0 — bed       (ambient loops; exempt from cap; never ducked)
   *   1 — breath    (atmospheric_breath; ducked by 3+, gated by 5)
   *   2 — feedback  (tap, cooldown; gated by 5)
   *   3 — event     (lesson, guide, small upgrade)
   *   4 — peak      (crystallize, epoch, big upgrade, golden caught)
   *   5 — voice     (sai activation; ducks all others to 0.2×)
   */
  readonly tier: 0 | 1 | 2 | 3 | 4 | 5;

  // ===== Anti-overlap / quality controls (JB 2026-05-24/25) =====

  /**
   * Minimum gap in ms between two consecutive plays of this event.
   * If the player triggers it sooner, the new play is silently dropped.
   * Default 0 = no throttle.
   */
  readonly minGapMs?: number;

  /**
   * Maximum number of concurrent instances of this event allowed to
   * play. New plays past the cap are dropped (or smooth-stopped if
   * forced via override). Default = Infinity (no cap).
   */
  readonly maxConcurrent?: number;

  /**
   * Fade-in duration in ms applied at play start.
   * Default 0 = no fade. Long for ambient/breath/peak.
   */
  readonly fadeInMs?: number;

  /**
   * Fade-out duration in ms applied just before the sound naturally
   * ends. The player schedules a fade `durationMs - fadeOutMs` after
   * start. Default 0 = hard end (avoid for everything per JB).
   */
  readonly fadeOutMs?: number;
}

/**
 * The manifest. Variant counts reflect actual .mp3 files present in
 * `apps/web/public/sounds/` after `bash scripts/process_sounds.sh`.
 *
 * Variant count comment shows N×. Add more by dropping numbered files in
 * the same dir + extending the array below.
 */
export const SOUND_MANIFEST: Readonly<Record<SoundEventId, SoundDefinition>> = {
  // ---------- UI (tier 2 — feedback) ----------
  // JB 2026-05-25: «ультракороткий звук на каждом тапе успешном и
  // неуспешном». Пока используем cooldown_ready файлы как fallback —
  // они E5 short shimmer, подходят. Когда JB сгенерит специальный
  // короткий click — заменим variants на /sounds/ui/tap_click/.
  tap_click: {
    variants: Array.from(
      { length: 20 },
      (_, i) => `/sounds/ui/cooldown_ready/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.18, // very subtle
    category: 'ui',
    tier: 2,
    minGapMs: 30,
    maxConcurrent: 2,
    fadeInMs: 5,
    fadeOutMs: 120,
  },
  tap_ding: {
    // 34× variants — JB generated a huge anti-repeat pool. We list all
    // so the player can rotate through them.
    variants: Array.from(
      { length: 34 },
      (_, i) => `/sounds/ui/tap_ding/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 1.0,
    category: 'ui',
    tier: 2,
    minGapMs: 80,
    maxConcurrent: 2,
    fadeInMs: 10,
    fadeOutMs: 80,
  },
  tap_ding_streak: {
    variants: Array.from(
      { length: 12 },
      (_, i) => `/sounds/ui/tap_ding_streak/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.9,
    category: 'ui',
    tier: 2,
    minGapMs: 80,
    maxConcurrent: 2,
    fadeInMs: 10,
    fadeOutMs: 80,
  },
  cooldown_ready: {
    variants: Array.from(
      { length: 20 },
      (_, i) => `/sounds/ui/cooldown_ready/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.4,
    category: 'ui',
    tier: 2,
    minGapMs: 100,
    maxConcurrent: 1,
    fadeInMs: 20,
    fadeOutMs: 200,
  },
  cooldown_charging: {
    variants: Array.from(
      { length: 13 },
      (_, i) => `/sounds/ui/cooldown_charging/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.35,
    category: 'ui',
    tier: 2,
    minGapMs: 800,
    maxConcurrent: 1,
    fadeInMs: 200,
    fadeOutMs: 400,
  },
  crystal_chord: {
    variants: Array.from(
      { length: 12 },
      (_, i) => `/sounds/ui/crystal_chord/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 1.0,
    category: 'ui',
    tier: 4,
    minGapMs: 500,
    maxConcurrent: 1,
    fadeInMs: 80,
    fadeOutMs: 800,
  },
  epoch_transition: {
    variants: Array.from(
      { length: 12 },
      (_, i) => `/sounds/ui/epoch_transition/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 1.0,
    category: 'ui',
    tier: 4,
    minGapMs: 1000,
    maxConcurrent: 1,
    fadeInMs: 300,
    fadeOutMs: 1200,
  },
  lesson_unlocked: {
    variants: Array.from(
      { length: 11 },
      (_, i) => `/sounds/ui/lesson_unlocked/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.9,
    category: 'ui',
    tier: 3,
    minGapMs: 200,
    maxConcurrent: 2,
    fadeInMs: 40,
    fadeOutMs: 300,
  },
  // ---------- Events (tier 3-4) ----------
  golden_spark_appears: {
    variants: Array.from(
      { length: 9 },
      (_, i) => `/sounds/events/golden_spark_appears/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.9,
    category: 'events',
    tier: 3,
    minGapMs: 1000,
    maxConcurrent: 1,
    fadeInMs: 50,
    fadeOutMs: 400,
  },
  golden_spark_caught: {
    variants: Array.from(
      { length: 12 },
      (_, i) => `/sounds/events/golden_spark_caught/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 1.0,
    category: 'events',
    tier: 4,
    minGapMs: 1000,
    maxConcurrent: 1,
    fadeInMs: 80,
    fadeOutMs: 600,
  },
  guide_appears: {
    variants: Array.from(
      { length: 10 },
      (_, i) => `/sounds/events/guide_appears/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.95,
    category: 'events',
    tier: 3,
    minGapMs: 1500,
    maxConcurrent: 1,
    fadeInMs: 400,
    fadeOutMs: 600,
  },
  sunrise_warm: {
    variants: Array.from(
      { length: 2 },
      (_, i) => `/sounds/events/sunrise_warm/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 1.0,
    category: 'events',
    tier: 3,
    fadeInMs: 800,
    fadeOutMs: 800,
  },
  season_arrives: {
    variants: Array.from(
      { length: 4 },
      (_, i) => `/sounds/events/season_arrives/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.9,
    category: 'events',
    tier: 3,
    fadeInMs: 500,
    fadeOutMs: 500,
  },
  // ---------- Voice (tier 5 — highest priority, ducks all) ----------
  sai_activation_start: {
    variants: Array.from(
      { length: 8 },
      (_, i) => `/sounds/voice/sai_activation_start/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.85,
    category: 'voice',
    tier: 5,
    minGapMs: 2000,
    maxConcurrent: 1,
    fadeInMs: 400,
    fadeOutMs: 800,
  },
  activation_text_typing: {
    variants: Array.from(
      { length: 4 },
      (_, i) => `/sounds/voice/activation_text_typing/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.3,
    category: 'voice',
    tier: 2,
    minGapMs: 100,
    maxConcurrent: 3,
    fadeInMs: 0,
    fadeOutMs: 100,
  },
  // ---------- Ambient loops (tier 0 — bed, exempt from cap) ----------
  ambient_epoch1: {
    // New A-minor batch — replaces the old B-minor placeholder.
    variants: Array.from(
      { length: 4 },
      (_, i) => `/sounds/ambient/ambient_epoch1/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.18,
    loop: true,
    category: 'ambient',
    tier: 0,
    fadeInMs: 2500,
    fadeOutMs: 2500,
  },
  ambient_epoch2: {
    variants: Array.from(
      { length: 4 },
      (_, i) => `/sounds/ambient/epoch2/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.18,
    loop: true,
    category: 'ambient',
    tier: 0,
    fadeInMs: 2500,
    fadeOutMs: 2500,
  },
  ambient_epoch3: {
    variants: Array.from(
      { length: 2 },
      (_, i) => `/sounds/ambient/epoch3/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.18,
    loop: true,
    category: 'ambient',
    tier: 0,
    fadeInMs: 2500,
    fadeOutMs: 2500,
  },
  // ---------- Atmospheric breath (tier 1 — random layer between ambient + foreground) ----------
  atmospheric_breath: {
    // 25× variants — 5 prompt types × ~5 generations each. Player rotates.
    variants: Array.from(
      { length: 25 },
      (_, i) => `/sounds/ambient/atmospheric_breath/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.3,
    category: 'ambient',
    tier: 1,
    minGapMs: 4000,
    maxConcurrent: 1,
    fadeInMs: 800,
    fadeOutMs: 1500,
  },
  // ---------- Upgrades ----------
  small_upgrade_purchased: {
    variants: Array.from(
      { length: 11 },
      (_, i) => `/sounds/upgrades/small_upgrade_purchased/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.85,
    category: 'upgrades',
    tier: 3,
    minGapMs: 300,
    maxConcurrent: 2,
    fadeInMs: 30,
    fadeOutMs: 250,
  },
  big_upgrade_purchased: {
    variants: Array.from(
      { length: 12 },
      (_, i) => `/sounds/upgrades/big_upgrade_purchased/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 1.0,
    category: 'upgrades',
    tier: 4,
    minGapMs: 800,
    maxConcurrent: 1,
    fadeInMs: 100,
    fadeOutMs: 700,
  },
  visible_change_reveal: {
    variants: Array.from(
      { length: 12 },
      (_, i) => `/sounds/upgrades/visible_change_reveal/v${String(i + 1).padStart(2, '0')}.mp3`,
    ),
    volume: 0.9,
    category: 'upgrades',
    tier: 4,
    minGapMs: 500,
    maxConcurrent: 1,
    fadeInMs: 80,
    fadeOutMs: 600,
  },
};

export const ALL_EVENT_IDS = Object.keys(SOUND_MANIFEST) as readonly SoundEventId[];
